import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { devWarn } from "../lib/log";

const EMPTY = { items: [], families: 0, total: 0, next_cursor: null, loaded: false };

// One flock's ducks on Your Ducks, grouped by family (the original + its branches), keyset-paged (GET /mine/ducks).
// A new flock/filter starts from the first page; bumping `reload` re-fetches as many families as are shown.
export const useFamilies = ({ flock, filters, enabled, reload = 0, pageSize = 12 }) => {
  const [state, setState] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const ref = useRef(state);
  ref.current = state;
  const lastQuery = useRef(null);
  const query = JSON.stringify({ flock, ...filters });

  const fetchPage = useCallback(
    (limit, cursor) => {
      const params = Object.fromEntries(Object.entries(JSON.parse(query)).filter(([, v]) => v));
      return api.get("/mine/ducks", { params: { ...params, limit, ...(cursor ? { cursor } : {}) } }).then((r) => r.data);
    },
    [query],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const same = lastQuery.current === query;
    lastQuery.current = query;
    if (!same) setState(EMPTY);
    setLoading(true);
    fetchPage(Math.min(50, Math.max(pageSize, same ? ref.current.items.length : 0)))
      .then((d) => alive && setState({ ...d, loaded: true }))
      .catch((e) => {
        devWarn("Your Ducks list failed", e?.message);
        if (alive) setState((s) => ({ ...s, loaded: true }));
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [enabled, fetchPage, query, reload, pageSize]);

  const loadMore = useCallback(async () => {
    const cur = ref.current;
    if (!cur.next_cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const more = await fetchPage(pageSize, cur.next_cursor);
      const seen = new Set(cur.items.map((g) => g.family_id));
      setState({ ...more, loaded: true, items: [...cur.items, ...more.items.filter((g) => !seen.has(g.family_id))] });
    } catch (e) {
      devWarn("Your Ducks load-more failed", e?.message);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchPage, loadingMore, pageSize]);

  return { ...state, loading, loadingMore, loadMore, hasMore: !!state.next_cursor };
};
